import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtQty = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => `${(parseFloat(n) || 0).toFixed(2)}%`;

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PRIMARY = '#4a148c';
const PRIMARY_MID = '#8e24aa';
const PRIMARY_LT = '#f3e5f5';
const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td = { padding: '7px 10px', textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };
const tdC = { padding: '7px 10px', textAlign: 'center', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

// Mini progress bar for margin %
const MarginBar = ({ value }) => {
  const pct = Math.min(Math.max(parseFloat(value) || 0, 0), 100);
  const color = pct >= 30 ? '#059669' : pct >= 15 ? '#d97706' : '#dc2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }}>
      <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color, minWidth: 40, textAlign: 'right' }}>{fmtPct(pct)}</span>
    </div>
  );
};

const RateAnalysisReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate] = useState(fmt2Date(today));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'StockReportApi',
        `/StockRateAnalysis/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch rate analysis report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      (r.ItemName || '').toLowerCase().includes(q) ||
      (r.GroupName || '').toLowerCase().includes(q) ||
      (r.Code || '').toLowerCase().includes(q) ||
      (r.BatchNumber || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  // Totals (item rows only — GroupName blank)
  const items = filtered.filter(r => r.ItemName && r.ItemName.trim() !== '');
  const totalStockValue = items.reduce((s, r) => s + (parseFloat(r.StockValue) || 0), 0);
  const totalSaleQty = items.reduce((s, r) => s + (parseFloat(r.SaleQty) || 0), 0);
  const totalStock = items.reduce((s, r) => s + (parseFloat(r.CurrentStock) || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'RateAnalysis', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Group', 'Item Name', 'Code', 'Barcode', 'Batch', 'Purchase Qty', 'Production Qty', 'Consumption Qty',
      'Sale Qty', 'Sale Ret', 'Pur Ret', 'Tr In Qty', 'Tr Out','Closing Qty', 'Avg Pur Rate', 'Sale Rate', 'MRP', 'Gross Margin', 'Margin%',
      'Last Pur Rate', 'Last Sale Rate', 'Bill Gross Margin', 'Bill Margin%', 'Stock Value'];
    const rows = filtered.map((r, i) => [
      i + 1, r.GroupName || '', r.ItemName || '', r.Code || '', r.Barcode || '', r.BatchNumber || '',
      parseFloat(r.CurrentStock) || 0, parseFloat(r.ProductionQty) || 0, parseFloat(r.ConsumptionQty) || 0,
      parseFloat(r.SaleQty) || 0, parseFloat(r.SaleReturnQty) || 0, parseFloat(r.PurchaseReturnQty) || 0, parseFloat(r.TransferInQty) || 0,
      parseFloat(r.TransferOutQty) || 0,parseFloat(r.ClosingQty) || 0,r.AvgPurchaseRate || '', r.SaleRate || '', r.MRP || '',
      parseFloat(r.GrossMargin) || 0, parseFloat(r.MarginPercentage) || 0,
      r.LastPurchaseRate || '', r.LastSaleRate || '',
      r.BillGrossMargin || '', r.BillMarginPercentage || '',
      parseFloat(r.StockValue) || 0,
    ]);

    // Grand Totals row
    const totalCurrentStockEx = filtered.reduce((s, r) => s + (parseFloat(r.CurrentStock) || 0), 0);
    const totalProductionQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.ProductionQty) || 0), 0);
    const totalConsumptionQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.ConsumptionQty) || 0), 0);
    //const totalPurchaseQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.CurrentStock) || 0), 0);
    const totalSaleQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.SaleQty) || 0), 0);
    const totalSaleReturnQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.SaleReturnQty) || 0), 0);
    const totalPurchaseReturnQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.PurchaseReturnQty) || 0), 0);
    const totalTransferInQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.TransferInQty) || 0), 0);
    const totalTransferOutQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.TransferOutQty) || 0), 0);
    const totalClosingQtyEx = filtered.reduce((s, r) => s + (parseFloat(r.ClosingQty) || 0), 0);
    const totalGrossMarginEx = filtered.reduce((s, r) => s + (parseFloat(r.GrossMargin) || 0), 0);
    const totalStockValueEx = filtered.reduce((s, r) => s + (parseFloat(r.StockValue) || 0), 0);

    const grandTotalsRow = [
      '', '', 'GRAND TOTAL', '', '', '',
      totalCurrentStockEx, totalProductionQtyEx, totalConsumptionQtyEx,
      totalSaleQtyEx, totalSaleReturnQtyEx, totalPurchaseReturnQtyEx,totalTransferInQtyEx, totalTransferOutQtyEx,
      totalClosingQtyEx, '', '','',
      totalGrossMarginEx, '',
      '', '', '', '',
      totalStockValueEx,
    ];

    await exportStyledExcel({
      reportTitle: 'RATE ANALYSIS REPORT',
      sheetName: 'Rate Analysis',
      fileName: `RateAnalysisReport_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      headers: cols,
      rows,
      totalsRow: grandTotalsRow,
      columnWidths: [4, 14, 24, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13, 13],
    });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <style>{`
        .rate-analysis-table-wrap::-webkit-scrollbar { height: 8px; width: 8px; }
        .rate-analysis-table-wrap::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 4px; }
        .rate-analysis-table-wrap::-webkit-scrollbar-thumb { background: #94a3b8; border-radius: 4px; }
        .rate-analysis-table-wrap::-webkit-scrollbar-thumb:hover { background: #64748b; }
      `}</style>
      <LogoLoader open={loading} message="Loading rate analysis report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #6a1b9a 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Rate Analysis Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search item, group, code..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="rate-analysis-table-wrap" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 130px)', scrollbarWidth: 'auto', scrollbarColor: '#94a3b8 #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1400 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={{ ...th, position: 'sticky', left: 0, zIndex: 4, background: PRIMARY_MID, minWidth: 36 }}>#</th>
                <th style={{ ...th, position: 'sticky', left: 36, zIndex: 4, background: PRIMARY_MID, minWidth: 100 }}>Group</th>
                <th style={{ ...th, position: 'sticky', left: 136, zIndex: 4, background: PRIMARY_MID, minWidth: 180 }}>Item Name</th>
                <th style={{ ...th, position: 'sticky', left: 316, zIndex: 4, background: PRIMARY_MID, minWidth: 80 }}>Code</th>
                {/* <th style={th}>Barcode</th> */}
                <th style={{ ...th, position: 'sticky', left: 396, zIndex: 4, background: PRIMARY_MID, minWidth: 80 }}>Batch</th>
                <th style={{ ...th, textAlign: 'right', position: 'sticky', left: 476,
                   zIndex: 4, background: PRIMARY_MID, minWidth: 90, boxShadow: '2px 0 5px rgba(0,0,0,0.15)' }}>Purchase Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Production Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Consumption Qty</th>
                {/* <th style={{ ...th, textAlign: 'right' }}>Purchase Qty</th> */}
                <th style={{ ...th, textAlign: 'right' }}>Sale Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Sale Ret</th>
                <th style={{ ...th, textAlign: 'right' }}>Pur Ret</th>
                <th style={{ ...th, textAlign: 'right' }}>Tr In</th>
                <th style={{ ...th, textAlign: 'right' }}>Tr Out</th>
                <th style={{ ...th, textAlign: 'right' }}>Closing Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Avg Pur Rate</th>
                <th style={{ ...th, textAlign: 'right' }}>Sale Rate</th>
                <th style={{ ...th, textAlign: 'right' }}>MRP</th>
                <th style={{ ...th, textAlign: 'right' }}>Gross Margin</th>
                <th style={{ ...th, textAlign: 'left', minWidth: 110 }}>Margin %</th>
                <th style={{ ...th, textAlign: 'right' }}>Last Pur Rate</th>
                <th style={{ ...th, textAlign: 'right' }}>Last Sale Rate</th>
                <th style={{ ...th, textAlign: 'right' }}>Bill Gross Margin</th>
                <th style={{ ...th, textAlign: 'left', minWidth: 110 }}>Bill Margin %</th>
                <th style={{ ...th, textAlign: 'right' }}>Stock Value</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={14} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (() => {
                let lastGroup = '';
                let lastItemId = null;
                let itemNo = 0;
                return filtered.map((r, i) => {
                  const isNewGroup = r.GroupName && r.GroupName.trim() !== '' && r.GroupName !== lastGroup;
                  const isSameItem = r.ItemId === lastItemId;
                  if (isNewGroup) lastGroup = r.GroupName;
                  // if (!isSameItem) { itemNo++; lastItemId = r.ItemId; }
                  const margin = parseFloat(r.MarginPercentage) || 0;
                  return (
                    <tr key={`r-${i}`} style={{ background: isSameItem ? '#eef2ff' : (itemNo % 2 === 0 ? '#fff' : PRIMARY_LT), borderBottom: '1px solid #f1f5f9', borderLeft: isSameItem ? `3px solid ${PRIMARY}` : 'none' }}>
                      <td style={{ ...td, position: 'sticky', left: 0, zIndex: 1, background: isSameItem ? '#eef2ff' : (itemNo % 2 === 0 ? '#fff' : PRIMARY_LT), minWidth: 36 }}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 700, color: PRIMARY, fontSize: 11, position: 'sticky', left: 36, zIndex: 1, background: isSameItem ? '#eef2ff' : (itemNo % 2 === 0 ? '#fff' : PRIMARY_LT), minWidth: 100 }}>{isNewGroup ? r.GroupName : ''}</td>
                      <td style={{ ...td, fontWeight: 600, paddingLeft: 10, minWidth: 180, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', position: 'sticky', left: 136, zIndex: 1, background: isSameItem ? '#eef2ff' : (itemNo % 2 === 0 ? '#fff' : PRIMARY_LT) }}>
                        {r.ItemName?.trim()}
                      </td>
                      <td style={{ ...td, color: '#64748b', fontSize: 11, position: 'sticky', left: 316, zIndex: 1, background: isSameItem ? '#eef2ff' : (itemNo % 2 === 0 ? '#fff' : PRIMARY_LT), minWidth: 80 }}>{r.Code || '-'}</td>
                      {/* <td style={{ ...td, color: '#64748b', fontSize: 11 }}>{r.Barcode || '-'}</td> */}
                      <td style={{ ...td, fontSize: 11, position: 'sticky', left: 396, zIndex: 1, background: isSameItem ? '#eef2ff' : (itemNo % 2 === 0 ? '#fff' : PRIMARY_LT), minWidth: 80 }}>
                        {r.BatchNumber ? <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: PRIMARY_LT, color: PRIMARY }}>{r.BatchNumber}</span> : '-'}
                      </td>
                      <td style={{ ...tdR, fontWeight: 600, color: PRIMARY, position: 'sticky', left: 476, zIndex: 1, background: isSameItem ? '#eef2ff' : (itemNo % 2 === 0 ? '#fff' : PRIMARY_LT), minWidth: 90, boxShadow: '2px 0 5px rgba(0,0,0,0.15)' }}>{fmtQty(r.CurrentStock)}</td>
                      <td style={{ ...tdR, color: '#059669' }}>{fmtQty(r.ProductionQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.ConsumptionQty)}</td>
                      {/* <td style={{ ...tdR, color: '#059669' }}>{fmtQty(r.CurrentStock)}</td> */}
                      <td style={tdR}>{fmtQty(r.SaleQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.SaleReturnQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.PurchaseReturnQty)}</td>
                      <td style={{ ...tdR, color: '#059669' }}>{fmtQty(r.TransferInQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.TransferOutQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.ClosingQty)}</td>
                      <td style={tdR}>{fmt(r.AvgPurchaseRate)}</td>
                      <td style={{ ...tdR, color: '#059669', fontWeight: 600 }}>{fmt(r.SaleRate)}</td>
                      <td style={tdR}>{fmt(r.MRP)}</td>
                      <td style={{ ...tdR, color: margin >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>{fmt(r.GrossMargin)}</td>
                      <td style={{ padding: '7px 10px' }}><MarginBar value={r.MarginPercentage} /></td>
                      <td style={{ ...tdR, color: '#64748b' }}>{fmt(r.LastPurchaseRate)}</td>
                      <td style={{ ...tdR, color: '#64748b' }}>{fmt(r.LastSaleRate)}</td>
                      <td style={{ ...tdR, color: margin >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>{fmt(r.BillGrossMargin)}</td>
                      <td style={{ padding: '7px 10px' }}><MarginBar value={r.BillMarginPercentage} /></td>
                      <td style={{ ...tdR, fontWeight: 700, color: PRIMARY }}>{fmt(r.StockValue)}</td>
                    </tr>
                  );
                });
              })()}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...td, textAlign: 'right', fontWeight: 700, position: 'sticky', left: 0, zIndex: 1, background: PRIMARY_LT }}>Grand Total</td>
                  <td style={{ ...tdR, position: 'sticky', left: 476, zIndex: 1, background: PRIMARY_LT, boxShadow: '2px 0 5px rgba(0,0,0,0.15)' }}>{fmtQty(totalStock)}</td>
                  <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.ProductionQty) || 0), 0))}</td>
                  <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.ConsumptionQty) || 0), 0))}</td>
                  {/* <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.CurrentStock) || 0), 0))}</td> */}
                  <td style={tdR}>{fmtQty(totalSaleQty)}</td>
                  <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.SaleReturnQty) || 0), 0))}</td>
                  <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.PurchaseReturnQty) || 0), 0))}</td>
                  <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.TransferInQty) || 0), 0))}</td>
                  <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.TransferOutQty) || 0), 0))}</td>
                  <td style={tdR}>{fmtQty(items.reduce((s, r) => s + (parseFloat(r.ClosingQty) || 0), 0))}</td>
                  <td style={tdR}></td>
                  <td style={tdR}></td>
                  <td style={tdR}></td>
                  <td style={tdR}>{fmt(items.reduce((s, r) => s + (parseFloat(r.GrossMargin) || 0), 0))}</td>
                  <td style={td}></td>
                  <td style={tdR}></td>
                  <td style={tdR}></td>
                  <td style={tdR}>{fmt(items.reduce((s, r) => s + (parseFloat(r.BillGrossMargin) || 0), 0))}</td>
                  <td style={td}></td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{fmt(totalStockValue)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default RateAnalysisReport;




